import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Download, Clock, CheckCircle2, AlertCircle, FileText, Plane, CreditCard, Sparkles, ChevronDown, ChevronUp, Users, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OrderMessages from "@/components/OrderMessages";
import ProfileTab from "@/components/ProfileTab";
import { categoryMeta, backendDocUrl } from "@/lib/docCategories";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { formatDestinationSummary, formatDateRange, formatOrderDate, applicantDisplayName } from "@/lib/applicants";
import { BUILD_DOCS_BY_ID } from "@/lib/buildDocs";
import { ADD_ONS } from "@/lib/addons";
import { toast } from "sonner";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

const docShortName = (id) => BUILD_DOCS_BY_ID[id]?.short || ADD_ONS.find((a) => a.id === id)?.short || id;

function ExpressCountdown({ order }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const paidAt = order.paid_at || order.updated_at || order.created_at;
  if (!paidAt) return null;
  const remaining = new Date(paidAt).getTime() + 3 * 3600000 - Date.now();
  return (
    <div
      data-testid={`express-countdown-${order.id}`}
      className="mt-3 flex items-center gap-2 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3"
    >
      <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
      {remaining <= 0
        ? "Finalizing your file — almost there"
        : `Your file arrives in ${remaining >= 3600000 ? `${Math.floor(remaining / 3600000)}h ` : ""}${Math.floor((remaining % 3600000) / 60000)}m`}
    </div>
  );
}

const STATUS_STYLES = {
  pending_payment: { label: "Pending Payment", className: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress: { label: "In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const STAGES = [
  { key: "payment", label: "Payment", icon: CreditCard },
  { key: "in_progress", label: "In Progress", icon: Sparkles },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

// Whether an order requires per-applicant passport details (any package, or custom orders that include the Visa Application Form).
function requiresApplicants(o) {
  if (!o) return false;
  return o.order_type !== "custom" || (o.selected_documents || []).includes("visa");
}

// True if applicants array isn't fully filled for the declared num_applicants.
function applicantsIncomplete(o) {
  const n = o?.num_applicants || 1;
  const arr = o?.applicants || [];
  if (arr.length < n) return true;
  return arr.some((a) => !a || !(a.passport_number || a.passport?.number));
}

function stageIndex(order) {
  if (order.status === "completed") return 2;
  if (order.payment_status === "paid" || order.status === "in_progress") return 1;
  return 0;
}

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tab = new URLSearchParams(location.search).get("tab") === "profile" ? "profile" : "orders";
  const setTab = (t) => navigate(t === "profile" ? "/dashboard?tab=profile" : "/dashboard", { replace: true });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [unreadMap, setUnreadMap] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/orders/my");
      setOrders(data);
      // load unread counts in parallel
      const counts = await Promise.all(
        data.map((o) => api.get(`/orders/${o.id}/messages`).then((r) => [o.id, r.data.unread || 0]).catch(() => [o.id, 0]))
      );
      const map = {};
      counts.forEach(([id, n]) => { map[id] = n; });
      setUnreadMap(map);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Bell dropdown deep-link: expand the order's messages panel
  useEffect(() => {
    const openOrder = location.state?.openOrder;
    if (openOrder) setExpanded(openOrder);
  }, [location.state]);

  const cancelOrder = async (orderId) => {
    if (!confirm("Cancel this pending order? It will be removed from your dashboard. (Admins can still see it for records.)")) return;
    try {
      await api.patch(`/orders/${orderId}/cancel`);
      toast.success("Order cancelled");
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel order");
    }
  };

  const openDoc = (orderId, index) => {
    window.open(backendDocUrl(orderId, index), "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <VerifyEmailBanner />
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] font-semibold">Dashboard</div>
              <h1 className="mt-1 font-display font-bold text-3xl text-[#0f1b2d]">Hello, {user?.name}</h1>
              <p className="mt-1 text-[#475569]">{tab === "profile" ? "Manage your account" : "Your visa files and orders"}</p>
            </div>
            {tab === "orders" && (
              <Link
                to="/apply"
                data-testid="dashboard-new-order"
                className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md transition-all"
              >
                <Plane className="w-4 h-4" /> New order
              </Link>
            )}
          </div>

          {/* Tab switcher */}
          <div className="mt-6 border-b border-[#e2e8f0] flex gap-1" data-testid="dashboard-tabs">
            {[
              { key: "orders", label: "Orders" },
              { key: "profile", label: "Profile" },
            ].map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`dashboard-tab-${t.key}`}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${active ? "border-[#2563eb] text-[#0f1b2d]" : "border-transparent text-[#64748b] hover:text-[#0f1b2d]"}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === "profile" ? (
            <div className="mt-8" data-testid="profile-view">
              <ProfileTab />
            </div>
          ) : (
          <div className="mt-8" data-testid="orders-list">
            {loading ? (
              <div className="text-[#475569]">Loading…</div>
            ) : orders.length === 0 ? (
              <div className="bg-white border border-[#e2e8f0] rounded-lg p-12 text-center">
                <div className="font-display font-bold text-xl text-[#0f1b2d]">No orders yet</div>
                <p className="mt-2 text-[#475569]">Start your first Schengen visa file in minutes.</p>
                <Link to="/apply" className="mt-6 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md">
                  Apply now
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => {
                  const st = STATUS_STYLES[o.status] || STATUS_STYLES.pending_payment;
                  const idx = stageIndex(o);
                  const open = expanded === o.id;
                  const unread = unreadMap[o.id] || 0;
                  return (
                    <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden">
                      <div className="p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="font-display font-bold text-lg text-[#0f1b2d]">
                                {o.package_name} · {o.destination_country}
                              </div>
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${st.className}`}>{st.label}</span>
                              {unread > 0 && (
                                <span data-testid={`unread-badge-${o.id}`} className="text-xs font-semibold px-2 py-1 rounded-full bg-red-500 text-white">
                                  {unread} new message{unread > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-[#475569]">
                              Order #{o.id.slice(-8)} · {formatDestinationSummary(o)} · {o.num_applicants} applicant(s){formatOrderDate(o.created_at) ? ` · ${formatOrderDate(o.created_at)}` : ""}
                            </div>
                            {o.order_type === "custom" && (o.selected_documents || []).length + (o.add_ons || []).length > 0 && (
                              <div className="mt-0.5 text-[11px] text-[#94a3b8]" data-testid={`order-docs-line-${o.id}`}>
                                {[...(o.selected_documents || []), ...(o.add_ons || [])].map(docShortName).join(" · ")}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-display font-black text-2xl text-[#0f1b2d]">${Math.round(o.amount)}</div>
                            <div className="text-xs text-[#475569] capitalize">{o.payment_status}</div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-6">
                          <div className="grid grid-cols-3 gap-2">
                            {STAGES.map((s, i) => {
                              const reached = i <= idx;
                              return (
                                <div key={s.key} className="flex items-center gap-2">
                                  <div className={`w-7 h-7 rounded-full grid place-items-center text-white text-xs font-bold ${reached ? "bg-[#2563eb]" : "bg-[#e2e8f0] text-[#94a3b8]"}`}>
                                    <s.icon className="w-3.5 h-3.5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className={`text-xs font-semibold ${reached ? "text-[#0f1b2d]" : "text-[#94a3b8]"}`}>{s.label}</div>
                                    {i < 2 && (
                                      <div className="h-0.5 mt-1 bg-[#e2e8f0] rounded">
                                        <div className={`h-0.5 rounded transition-all ${i < idx ? "bg-[#2563eb] w-full" : "bg-[#2563eb] w-0"}`} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Status banners */}
                        {o.status === "completed" && (
                          <div data-testid={`ready-banner-${o.id}`} className="mt-5 flex items-center gap-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-4">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            <div>
                              <div className="font-semibold">Your file is ready! Download below.</div>
                              <div className="text-xs text-emerald-700/80">Bring these documents to your appointment.</div>
                            </div>
                          </div>
                        )}
                        {o.status === "pending_payment" && (
                          <div className="mt-5 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid={`pending-banner-${o.id}`}>
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div>Payment not completed yet.</div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <RouterLink
                                  to={`/apply/review/${o.id}`}
                                  data-testid={`resume-order-${o.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md"
                                >
                                  Resume & pay
                                </RouterLink>
                                <button
                                  onClick={() => cancelOrder(o.id)}
                                  data-testid={`cancel-order-${o.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 px-3 py-1.5 rounded-md"
                                >
                                  Cancel order
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {o.payment_status === "paid" && o.delivery_type === "express" && o.status !== "completed" && (
                          <ExpressCountdown order={o} />
                        )}
                        {o.status === "in_progress" && (
                          <div className="mt-5 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
                            <Clock className="w-4 h-4" />
                            Your file is being prepared. You&apos;ll be notified on WhatsApp as soon as it&apos;s ready.
                          </div>
                        )}

                        {/* Applicant-details required banner: paid orders that still need passport info */}
                        {(o.payment_status === "paid" || o.status === "in_progress") && o.status !== "completed" && requiresApplicants(o) && applicantsIncomplete(o) && (
                          <div data-testid={`applicants-required-banner-${o.id}`} className="mt-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-amber-900">
                                {o.order_type === "custom" ? "Your Visa Application Form requires your passport details." : "We still need your applicant details to start preparing your file."}
                              </div>
                              <div className="text-xs text-amber-800/90 mt-0.5">Please complete your applicant information to proceed.</div>
                              <RouterLink
                                to={`/apply/applicants/${o.id}`}
                                data-testid={`complete-applicants-${o.id}`}
                                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md"
                              >
                                Complete applicant details →
                              </RouterLink>
                            </div>
                          </div>
                        )}

                        {/* Documents — grouped by applicant */}
                        {o.documents && o.documents.length > 0 && (
                          <div className="mt-5 border-t border-[#e2e8f0] pt-5">
                            <div className="text-xs uppercase tracking-widest text-[#64748b] mb-3">Your documents</div>
                            <DocsByApplicant order={o} onOpen={(idx) => openDoc(o.id, idx)} />
                          </div>
                        )}

                        <button
                          onClick={() => setExpanded(open ? null : o.id)}
                          data-testid={`toggle-messages-${o.id}`}
                          className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#2563eb]"
                        >
                          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {open ? "Hide messages" : "Open messages"}
                          {!open && unread > 0 && <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">{unread}</span>}
                        </button>
                        {o.status !== "completed" && requiresApplicants(o) && (
                          <RouterLink
                            to={`/apply/applicants/${o.id}`}
                            data-testid={`edit-applicants-${o.id}`}
                            className="mt-5 ml-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0f1b2d] hover:text-[#2563eb]"
                          >
                            <Users className="w-4 h-4" /> Edit applicant details
                          </RouterLink>
                        )}
                      </div>

                      {open && (
                        <div className="border-t border-[#e2e8f0] p-6 bg-[#f8fafc]">
                          <OrderMessages
                            orderId={o.id}
                            role="customer"
                            onUnreadChange={(n) => setUnreadMap((m) => ({ ...m, [o.id]: n }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function DocsByApplicant({ order, onOpen }) {
  const applicants = order.applicants || [];
  const docs = order.documents || [];

  // Group by applicant_index
  const groups = new Map();
  docs.forEach((d, i) => {
    const key = (typeof d.applicant_index === "number") ? d.applicant_index : "general";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ ...d, _idx: i });
  });

  const renderGroup = (label, items, key) => (
    <div key={key} className="space-y-2">
      <div className="text-xs font-semibold text-[#0f1b2d] flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-full bg-[#eff6ff] text-[#2563eb]">{label}</span>
        <span className="text-[10px] text-[#64748b]">{items.length} doc{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((d) => {
          const cat = categoryMeta(d.category);
          return (
            <button
              key={d._idx}
              data-testid={`download-doc-${order.id}-${d._idx}`}
              onClick={() => onOpen(d._idx)}
              className="flex items-center gap-3 p-3 rounded-md border border-[#e2e8f0] hover:border-[#2563eb] hover:bg-[#eff6ff] transition text-left"
            >
              <FileText className="w-4 h-4 text-[#2563eb] flex-shrink-0" />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#eff6ff] text-[#2563eb] text-[11px] font-semibold flex-shrink-0">
                <span>{cat.emoji}</span><span>{cat.value}</span>
              </span>
              <span className="text-sm text-[#0f1b2d] truncate flex-1">{d.original_filename}</span>
              <Download className="w-4 h-4 text-[#475569]" />
            </button>
          );
        })}
      </div>
    </div>
  );

  const sections = [];
  applicants.forEach((a, i) => {
    const items = groups.get(i) || [];
    if (items.length === 0) return; // skip empty applicant groups
    sections.push(renderGroup(`Applicant ${i + 1} — ${applicantDisplayName(a) || "(no name)"}`, items, `app-${i}`));
  });
  const general = groups.get("general") || [];
  if (general.length > 0) sections.push(renderGroup("General / shared", general, "general"));

  if (sections.length === 0) {
    // Fallback: just show all docs flat (shouldn't happen since parent checks length, but defensive)
    return renderGroup("All documents", docs.map((d, i) => ({ ...d, _idx: i })), "all");
  }
  return <div className="space-y-4">{sections}</div>;
}

